import { ICerbosData, IMainContext } from "@/ts/common-types";
import {
    cellRendererCerbosResourcesAndActions,
    mainContext,
    permissionConfig,
} from "@/ts/constants";
import { checkPermissionIsAllowed, getActiveModule } from "@/ts/utils";
import { useContext, useMemo } from "react";

interface IUsePermissionCheckParams {
    type?: string;
}

const usePermissionCheck = ({ type }: IUsePermissionCheckParams = {}) => {
    const { cerbosPermissions } = useContext<IMainContext>(mainContext);
    const { activeModule } = getActiveModule();

    const getCombinedPermissionsConfig = () => {
        const commonPermissions = permissionConfig.commonPermissions || [];
        const modulePermissions = permissionConfig[activeModule] || [];
        return [...commonPermissions, ...modulePermissions];
    };

    const getPermissionsForCellRenderer = () => {
        const cerbosConfig =
            cellRendererCerbosResourcesAndActions[activeModule];
        if (cerbosConfig) {
            return cerbosConfig.reduce<Record<string, boolean>>(
                (result, { resource, action, key }) => {
                    const hasPermission = checkPermissionIsAllowed(
                        cerbosPermissions as ICerbosData,
                        { kind: resource, id: resource },
                        action
                    );
                    result[key] = !hasPermission;
                    return result;
                },
                {}
            );
        } else {
            return { isModuleEditDisabled: false };
        }
    };

    const getGeneralPermissions = () => {
        const combinedConfig = getCombinedPermissionsConfig();
        return combinedConfig.reduce<Record<string, boolean>>(
            (result, { resource, action, key }) => {
                result[key] = checkPermissionIsAllowed(
                    cerbosPermissions as ICerbosData,
                    { kind: resource, id: resource },
                    action
                );
                return result;
            },
            {}
        );
    };

    return useMemo(() => {
        if (type === "cellRenderer") {
            return getPermissionsForCellRenderer();
        } else {
            return getGeneralPermissions();
        }
    }, [cerbosPermissions, activeModule]);
};

export default usePermissionCheck;
